import { Dispatch, SetStateAction, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { UserKeys, deleteOrgUnit } from '../../../api/users-api';
import { handleErrors } from '../../../utils/errors';
import SharedDialog from '../../../shared/shared-dialog';
import ChangeLogDialog from '../../../shared/change-log-dialog';
import { useStore } from '../../state/zustand-store';
import { OrgUnitModel } from '../../../../types';
import { useAnalytics } from '../../../shared/analytics/create-events';
import * as analytics from '../../../shared/analytics/analytics.json';
import { ModuleList } from '../../../shared/analytics/module-list';
import {
  DirectoryRoles,
  isAbleToSeeUIElement,
} from '../../../utils/roles/client-roles';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
interface DeleteOrgUnitDialogProps {
  open: boolean;
  handleClose: () => void;
  selectedOrgUnit: OrgUnitModel;
  changeLogRoles: string[];
  setSelectedOrgUnitId: Dispatch<SetStateAction<string>>;
}

export default function DeleteOrgUnitDialog({
  open,
  handleClose,
  selectedOrgUnit,
  changeLogRoles,
  setSelectedOrgUnitId,
}: DeleteOrgUnitDialogProps) {
  const { createEvent } = useAnalytics();
  const { setSnackbar } = useStore();
  const queryClient = useQueryClient();
  const [openChangeLogDialog, setOpenChangeLogDialog] = useState(false);
  const [changeLogMessage, setChangeLogMessage] = useState('');

  const copiedOrg = { ...selectedOrgUnit };
  delete copiedOrg.children;

  const { mutate, isPending } = useMutation({
    mutationFn: () => deleteOrgUnit(copiedOrg, changeLogMessage),
    onSuccess: () => {
      setSnackbar({
        open: true,
        severity: 'success',
        message: 'The organization has been deleted successfully!',
      });

      setSelectedOrgUnitId('/');

      // This sets the tree data so it gets the latest changes
      queryClient.setQueryData<OrgUnitModel[] | undefined>(
        [UserKeys.OrgUnitModels],
        (oldData) => {
          if (!oldData) return [];
          return oldData.filter(
            (org) => org.orgUnitId !== selectedOrgUnit.orgUnitId
          );
        }
      );

      // This invalidates all autocompletes so they get the latest changes
      queryClient.invalidateQueries({
        queryKey: [UserKeys.OrgUnitsFromGoogle],
      });

      setChangeLogMessage('');
      handleClose();
    },
    onError: (error) => {
      handleErrors({
        error,
        snackbarMessage:
          'There was an issue deleting the organization. Please refresh and try again.',
      });
    },
    onSettled: () => {
      createEvent(analytics.directory.organizational_units.delete_org_unit, {
        module: ModuleList.modules.directory.name,
        section: ModuleList.modules.directory.sections.organizationalUnits,
      });
      setOpenChangeLogDialog(false);
    },
  });

  function handleSubmit() {
    if (
      isAbleToSeeUIElement(changeLogRoles, DirectoryRoles.RemoveOrgUnit.value)
    ) {
      setOpenChangeLogDialog(true);
    } else {
      mutate();
    }
  }

  return (
    <SharedDialog
      open={open}
      handleClose={handleClose}
      title="Delete Organizational Unit"
      dialogContent={
        <Box>
          <Typography
            sx={{
              mt: 1,
            }}
          >
            Delete <strong>{selectedOrgUnit.name}</strong>?
          </Typography>
          <ChangeLogDialog
            open={openChangeLogDialog}
            handleClose={() => setOpenChangeLogDialog(false)}
            changeLogMessage={changeLogMessage}
            setChangeLogMessage={setChangeLogMessage}
            mutate={mutate}
            mutateLoading={isPending}
          />
        </Box>
      }
      primaryButton={{
        title: 'DELETE ORG UNIT',
        action: handleSubmit,
        loading: isPending,
        disabled: isPending,
        color: 'error',
      }}
    />
  );
}
